import {useStage} from "@client/components/Stage";
import {changeSearchFilter} from "@client/features/search";
import {createRouter, routerFactory} from "@client/gotterdammerung";
import {useAppDispatch, useAppSelector} from "@client/store";
import {server} from "@reactivated";
import React from "react";

import {usePredictions} from "./predictions";

export interface EntryJourneyInput {
    target:
        | {type: "DAY"; day: server.journal.nutrition.Day.output}
        | {type: "RECIPE"; recipe: server.journal.nutrition.Recipe.output}
        | null;
    highlightIcons?: boolean;
    preSelectedMealName?: string;
    cameraEnabled?: boolean;
    initial?:
        | {
              type: "entries";
              entries: Array<server.journal.nutrition.ExpandedEntry.output>;
          }
        | {
              type: "food_from_search";
              upc: string;
              quantity: null;
              unit: null;
          };
}

export type EntryJourneyOutput =
    | {
          type: "entries";
          entries: Array<server.journal.nutrition.ExpandedEntry.output>;
          meal: server.journal.nutrition.StagedMeal.output;
          method: "now" | "cupboard";
      }
    | {type: "switch_to_camera"; captureType: "meal" | "barcode"};

function useEntryHooks(input: EntryJourneyInput) {
    const dispatch = useAppDispatch();
    const [recentRoute, setRecentRoute] = React.useState<
        "roots" | "predictions" | "customize"
    >("roots");

    const stage = useStage<server.journal.nutrition.ExpandedEntry.output>({
        entries: input.initial?.type === "entries" ? input.initial.entries : [],
        target: input.target,
        preSelectedMealName: input.preSelectedMealName,
    });

    const state = useAppSelector((state) => ({
        predictions: state.search.predictions ?? {entries: {}, roots: []},
        results: state.search.stores.recent,
    }));
    const predictions = usePredictions(state.predictions, state.results);

    // Clear search filter when the entry journey opens
    React.useEffect(() => {
        dispatch(changeSearchFilter(""));
    }, []);

    const [foodFromSearch, setFoodFromSearch] = React.useState<{
        upc: string | null;
        quantity: DecimalValue | null;
        unit: string | null;
    } | null>(
        input.initial?.type === "food_from_search"
            ? {upc: input.initial.upc, quantity: null, unit: null}
            : null,
    );
    const [fromScan, setFromScan] = React.useState(input.initial != null);

    return {
        stage,
        predictions,
        foodFromSearch,
        setFoodFromSearch,
        fromScan,
        setFromScan,
        recentRoute,
        setRecentRoute,
    };
}

export const Search = routerFactory<EntryJourneyInput, EntryJourneyOutput>()(
    ({close, ...input}) => {
        const hooks = useEntryHooks(input);

        return {input, close, ...hooks};
    },
)({
    name: "search",
    path: "",
    tabs: ["recent", "database", "foods", "recipes"],
});

export const Create = Search.subroute({
    name: "create",
    path: "create/",
    tabs: ["macros", "recipe"],
});

export const CreateFood = Search.subroute({
    name: "create_food",
    path: "corrections/",
    tabs: ["macros", "advanced"],
});

export const BarcodeFoundConfirmEntry = Search.subroute({
    name: "barcode_found_confirm_entry",
    path: "barcode/",
    tabs: ["macros"],
});

export const BarcodeFoundMakeCorrections = Search.subroute({
    name: "barcode_found_make_corrections",
    path: "barcode/corrections/",
    tabs: ["macros", "advanced"],
});

export const BarcodeNotFoundCreateFood = Search.subroute({
    name: "barcode_not_found_create_food",
    path: "barcode-not-found/",
    tabs: ["macros", "advanced"],
});

export const BarcodeNotFoundConfirmEntry = Search.subroute({
    name: "barcode_not_found_confirm_entry",
    path: "barcode-not-found/confirm/",
    tabs: ["macros"],
});

export const router = createRouter(
    [
        Search,
        Create,
        CreateFood,
        BarcodeFoundConfirmEntry,
        BarcodeFoundMakeCorrections,
        BarcodeNotFoundCreateFood,
        BarcodeNotFoundConfirmEntry,
    ],
    "memory",
);

export type Router = typeof router;
